/**
 * Hooks Barrel Export
 * 모든 커스텀 훅을 한 곳에서 import 가능하도록
 */

// ============================================================================
// Main Hooks
// ============================================================================
export { useAuth } from './useAuth';

// ============================================================================
// Channel Context
// ============================================================================
export { useChannelContext, makeChannelHref } from './useChannelContext';
export type { UseChannelContextReturn } from './useChannelContext';

// ============================================================================
// Page-Specific Hooks
// ============================================================================
export { usePlaylistModals } from './usePlaylistModals';
export type { UsePlaylistModalsReturn, ModalType } from './usePlaylistModals';

export { useChannelModals } from './useChannelModals';
export type { UseChannelModalsReturn, ChannelModalType } from './useChannelModals';

export { usePlaylistActions } from './usePlaylistActions';
export type { UsePlaylistActionsReturn, UsePlaylistActionsOptions } from './usePlaylistActions';

export { useMusicVideoActions } from './useMusicVideoActions';
export type { UseMusicVideoActionsReturn, UseMusicVideoActionsOptions } from './useMusicVideoActions';

export { useAudioArrangementActions } from './useAudioArrangementActions';
export type { UseAudioArrangementActionsReturn, UseAudioArrangementActionsOptions } from './useAudioArrangementActions';

export { useTrackActions } from './useTrackActions';
export type { UseTrackActionsReturn, UseTrackActionsOptions } from './useTrackActions';

// ============================================================================
// AppToast / Notifications
// ============================================================================
export { useToast } from './useToast';
export type { UseToastReturn, ToastType } from './useToast';

export { useErrorToast, useMultipleErrorToasts } from './useErrorToast';
export type { UseErrorToastOptions } from './useErrorToast';

// ============================================================================
// WebSocket / Real-time Updates
// ============================================================================
export {
  useWebSocket,
  // 타입 가드 함수들 (WebSocket 메시지 타입 안전성)
  isProgressUpdate,
  isTrackProgress,
  isErrorNotification,
  isCompletionNotification,
  isLogMessage,
  isControlResponse,
  isCostUpdate,
  isStatsUpdate,
  isInitialDashboardData,
} from './websocket';
export type {
  UseWebSocketReturn,
  UseWebSocketOptions,
  WebSocketStatus,
  WebSocketMessage,
  ProgressUpdate,
  TrackProgress,
  ErrorNotification,
  CompletionNotification,
  LogMessage,
  VersionCreated,
  ControlResponse,
  CostUpdateMessage,
  StatsUpdateMessage,
  InitialDashboardMessage,
} from './websocket';

// ============================================================================
// Query Hooks (TanStack Query) - Orval 생성 훅 사용
// ============================================================================
// Note: Orval에서 생성된 훅은 '@/lib/api/orval/hooks'에서 직접 import하세요.
// 예: import { usePlaylistList, usePlaylistRetrieve } from '@/lib/api/orval';

// 커스텀 훅: 복제 기능
export { useDuplicatePlaylist } from './useDuplicatePlaylist';
export type { DuplicatePlaylistRequest } from './useDuplicatePlaylist';

// 커스텀 훅: 트랙 버전 액션
export {
  useSelectTrackVersion,
  useToggleTrackVersionInclusion,
  useExtendTrackVersion,
  useMoveTrackVersion,
  useSplitTrackVersion,
} from './useTrackVersionActions';

// 커스텀 훅: 트랙 버전 편집 (제목, 다운로드, 재생시간 등 공통 로직)
export { useTrackVersionEdit } from './useTrackVersionEdit';
export type { UseTrackVersionEditReturn } from './useTrackVersionEdit';

// ============================================================================
// DnD Grid
// ============================================================================
export { useSortableGrid } from './useSortableGrid';
export type { UseSortableGridOptions, UseSortableGridReturn } from './useSortableGrid';

// ============================================================================
// Track Monitoring & Batch Actions
// ============================================================================
export { useTrackRegeneration } from './useTrackRegeneration';
export type { UseTrackRegenerationOptions, UseTrackRegenerationReturn } from './useTrackRegeneration';

export { useTrackBatchActions } from './useTrackBatchActions';
export type { UseTrackBatchActionsOptions, UseTrackBatchActionsReturn } from './useTrackBatchActions';

// ============================================================================
// Clipboard
// ============================================================================
export { useCopyToClipboard } from './useCopyToClipboard';
export type { UseCopyToClipboardReturn } from './useCopyToClipboard';

// ============================================================================
// Form Utilities
// ============================================================================
export { useFormReset, useConditionalFormReset } from './useFormReset';
export { useZodForm } from './useZodForm';
export type { ZodFormReturn } from './useZodForm';

// ============================================================================
// YouTube Hooks
// ============================================================================
export { useYouTubeUpload } from './useYouTubeUpload';
export { useYouTubeSuggestions } from './useYouTubeSuggestions';

